import {
  Client, Reserve,
} from '../api'
import { ClientV2, ReserveV2 } from "../api-v2"
import { ClientMapper } from '../api-v2/mappings'
import { ApiVersionDelagator } from "./api-delegator"
/**
 * @typedef {import('../api-v2/js-doc').V1UpdateClientRequest} V1UpdateClientRequest
 * @typedef {import('../api-v2/js-doc').V1ReserveRateHistoryResponse} V1ReserveRateHistoryResponse
 * @typedef {import('../api-v2/js-doc').V2ClientFactoringConfigResponse} V2ClientFactoringConfigResponse
 * @typedef {import('../api-v2/js-doc').V2MappingOptions} V2MappingOptions
 * @typedef {import('../api-v2/js-doc').V2ReserveTotalResponse} V2ReserveTotalResponse
 * @typedef {import('../api-v2/js-doc').V1ReserveTotalResponse} V1ReserveTotalResponse
 */

export class ClientApiDelegator extends ApiVersionDelagator {
  /**
   * @param {string} clientId - ID of client
   * @param {V1UpdateClientRequest} v1Request - V1 request
   * @returns {Promise<any>}
   */
  async updateClientProfile (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      async (_v1Request) => {
        return await this.#updateClientProfileV1(clientId, _v1Request)
      },
      async (v2MappingOptions, _v1Request) => {
        return await this.#updateClientProfileV2(v2MappingOptions, clientId, _v1Request)
      },
      {
        operationName: 'update-client-profile',
      }
    )
  }

  /**
   * @param {string} clientId - ID of client
   * @param {V1UpdateClientRequest} payload - V1 request
   * @returns {Promise<any>}
   */
  async #updateClientProfileV1 (clientId, payload) {
    return (await Client.update({
      ...payload,
      id: clientId,
    })).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {string} clientId - ID of client
   * @param {V1UpdateClientRequest} v1Payload - V1 request
   * @returns {Promise<V2ClientFactoringConfigResponse>}
   */
  async #updateClientProfileV2 (v2MappingOptions, clientId, v1Payload) {
    const v2Payload = ClientMapper.v1UpdateClientRequestToV2(v2MappingOptions, v1Payload)
    return new ClientV2().updateConfiguration(clientId, v2Payload)
  }

  /**
   * @param {string} clientId - ID of client
   * @param {V1UpdateClientRequest} v1Request - V1 request
   * @returns {Promise<any>}
   */
  async updateClientStatus (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      async (_v1Request) => {
        return await this.#updateClientStatusV1(clientId, _v1Request)
      },
      async (v2MappingOptions, _v1Request) => {
        return await this.#updateClientStatusV2(v2MappingOptions, clientId, _v1Request)
      },
      {
        operationName: 'update-client-status',
      }
    )
  }

  /**
   * @param {string} clientId - ID of client
   * @param {V1UpdateClientRequest} payload - V1 request
   * @returns {Promise<any>}
   */
  async #updateClientStatusV1 (clientId, payload) {
    return (await Client.update({
      ...payload,
      id: clientId,
    })).data
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {string} clientId - ID of client
   * @param {V1UpdateClientRequest} v1Payload - V1 request
   * @returns {Promise<any>}
   */
  async #updateClientStatusV2 (v2MappingOptions, clientId, v1Payload) {
    const v2Payload = ClientMapper.v1UpdateClientRequestToV2(v2MappingOptions, v1Payload)
    return new ClientV2().updateConfiguration(clientId, v2Payload)
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveRateHistoryResponse[]>}
   */
  async getReserveRateHistory (clientId) {
    return this.runGetApiCalls(() => {
      return this.#getReserveRateHistoryV1(clientId)
    }, () => {
      return this.#getReserveRateHistoryV2(clientId)
    })
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveRateHistoryResponse[]>}
   */
  async #getReserveRateHistoryV1 (clientId) {
    return (await Client.history({
      fields: 'reserve_rate_percentage',
      id: clientId,
    })).data?.rows || []
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveRateHistoryResponse[]>}
   */
  async #getReserveRateHistoryV2 (clientId) {
    const result = await (new ClientV2().getOne(clientId))
    return result.factoringConfig.reserveRateHistory.map((e) => {
      return ClientMapper.v2ReserveRateHistoryToV1(e)
    })
  }
  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveTotalResponse>}
   */
  async getReserveTotal (clientId) {
    return this.runGetApiCalls(() => {
      return this.#getReserveTotalV1(clientId)
    }, () => {
      return this.#getReserveTotalV2(clientId)
    })
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveTotalResponse>}
   */
  async #getReserveTotalV1 (clientId) {
    return (await Reserve.getClientTotalReserve(clientId)).data
  }

  /**
   * @param {string} clientId - ID of the client
   * @returns {Promise<V1ReserveTotalResponse>}
   */
  async #getReserveTotalV2 (clientId) {
    const result = await (new ReserveV2(clientId).total())
    return ClientMapper.v2ReserveTotalToV1(result)
  }
}
